import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { Box } from '@wix/design-system';
import { useHistory } from 'react-router-dom';
import { AnonymousFooter } from '../../../Components/AnonymousFooter/AnonymousFooter';
import { BusinessNamesSection } from '../../../Components/BusinessNames';
import { BngResultsHeader } from '../../../Components/Headers';
import { RecommendationsError } from '../../../Components/Recommendations';
import { useIsMobile } from '../../../hooks/useIsMobile';
import pageStyles from '../../anonymousResultsPage.scss';
import BngDomainSuggestionsSection from './Components/BngDomainSuggestionsSection';
import BngPageLoadError from './Components/BngPageLoadError';
import { BngResultsPageShell, DESKTOP_STICKY_SEARCH_TOP_GAP } from './Components/BngResultsPageShell';
import { useBusinessNamesBi } from './hooks/useBusinessNamesBi';
import { useBngResults } from './hooks/useBngResults';
import { useUrlQueryStoreSync } from './hooks/useUrlQueryStoreSync';
import { useSearchField } from './hooks/useSearchField';
import { useTwoStepFlow } from './hooks/useTwoStepFlow';
import { useScrollToDomainsHeading } from './hooks/useScrollToDomainsHeading';
import { syncQueryToStore } from './utils/syncQueryToStore';
const MOBILE_STICKY_SEARCH_TOP_GAP = '10px';
const MOBILE_STICKY_SEARCH_BOTTOM_GAP = '10px';
const MOBILE_DOMAINS_STEP_STICKY_SEARCH_BOTTOM_GAP = '28px';
export const BngResultsPageContentV1 = _ref => {
  let _ref$showCartHeader = _ref.showCartHeader,
    showCartHeader = _ref$showCartHeader === void 0 ? false : _ref$showCartHeader;
  const isMobile = useIsMobile();
  const twoStep = isMobile;
  const queryFromUrl = useUrlQueryStoreSync();
  const _useTwoStepFlow = useTwoStepFlow({
      enabled: twoStep,
      queryFromUrl
    }),
    mobileStep = _useTwoStepFlow.mobileStep,
    namesEntering = _useTwoStepFlow.namesEntering,
    stepAnimating = _useTwoStepFlow.stepAnimating,
    isNamesStepVisible = _useTwoStepFlow.isNamesStepVisible,
    isDomainsStepVisible = _useTwoStepFlow.isDomainsStepVisible,
    resetToNames = _useTwoStepFlow.resetToNames,
    goToDomains = _useTwoStepFlow.goToDomains,
    goBackToNames = _useTwoStepFlow.goBackToNames;
  const _useBngResults = useBngResults({
      queryFromUrl,
      twoStep: twoStep ? {
        isDomainsStepVisible
      } : undefined
    }),
    hasBusinessNamesError = _useBngResults.hasBusinessNamesError,
    pageLoadErrorMessage = _useBngResults.pageLoadErrorMessage,
    retryBusinessNames = _useBngResults.retryBusinessNames,
    hasRecommendations = _useBngResults.hasRecommendations,
    taxesNotIncluded = _useBngResults.taxesNotIncluded,
    domainSuggestionsProps = _useBngResults.domainSuggestionsProps;
  const _useBusinessNamesBi = useBusinessNamesBi(queryFromUrl),
    onNamesShown = _useBusinessNamesBi.onNamesShown,
    reportBackToNames = _useBusinessNamesBi.reportBackToNames;
  const searchField = useSearchField({
    queryFromUrl,
    onGenerate: twoStep ? resetToNames : undefined
  });
  const history = useHistory();
  const _useScrollToDomainsHe = useScrollToDomainsHeading(),
    stickyHeaderRef = _useScrollToDomainsHe.stickyHeaderRef,
    domainsHeadingRef = _useScrollToDomainsHe.domainsHeadingRef,
    scrollToDomainsHeading = _useScrollToDomainsHe.scrollToDomainsHeading;
  const handleUserSelectedName = useCallback(name => {
    if (twoStep) {
      syncQueryToStore(name);
      goToDomains();
      return;
    }
    scrollToDomainsHeading();
  }, [twoStep, goToDomains, scrollToDomainsHeading]);
  const handleBackToNames = useCallback(() => {
    reportBackToNames();
    goBackToNames();
  }, [reportBackToNames, goBackToNames]);
  if (pageLoadErrorMessage) {
    return /*#__PURE__*/React.createElement(BngPageLoadError, {
      onRetry: retryBusinessNames,
      message: pageLoadErrorMessage
    });
  }
  const shouldDisplayFooter = !hasBusinessNamesError && !!queryFromUrl && hasRecommendations && isDomainsStepVisible;
  return /*#__PURE__*/React.createElement(BngResultsPageShell, {
    layout: {
      pagePaddingSides: isMobile ? 'SP4' : '48px',
      pagePaddingTop: showCartHeader ? '0' : 'SP5',
      stickyBarPaddingTop: isMobile ? MOBILE_STICKY_SEARCH_TOP_GAP : DESKTOP_STICKY_SEARCH_TOP_GAP,
      stickyBarPaddingBottom: isMobile ? mobileStep === 'domains' ? MOBILE_DOMAINS_STEP_STICKY_SEARCH_BOTTOM_GAP : MOBILE_STICKY_SEARCH_BOTTOM_GAP : '0px',
      headerPaddingBottom: isMobile ? 'SP3' : undefined
    },
    header: /*#__PURE__*/React.createElement(BngResultsHeader, {
      onBackClick: history.goBack
    }),
    searchField: searchField,
    stickyHeaderRef: stickyHeaderRef,
    clipHorizontalOverflow: twoStep && stepAnimating
  }, queryFromUrl && /*#__PURE__*/React.createElement(Box, {
    gridArea: "names",
    className: clsx(pageStyles.centeredContent, twoStep && namesEntering && pageStyles.bngNamesStepEnter),
    display: isNamesStepVisible ? 'block' : 'none'
    // 6px + the 24px grid row-gap = 30px above the names title.
    ,
    paddingTop: isMobile ? 'SP3' : '6px'
  }, /*#__PURE__*/React.createElement(BusinessNamesSection, {
    query: queryFromUrl,
    presentation: twoStep ? 'list' : 'grid',
    onSelectedNameChange: syncQueryToStore,
    onUserSelectedName: handleUserSelectedName,
    onNamesShown: onNamesShown
  })), !hasBusinessNamesError && (!queryFromUrl ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: "NoRecommendations",
    onRetry: domainSuggestionsProps.onRetry,
    type: "anonymous",
    gridArea: "content",
    className: pageStyles.centeredContent
  }) : isDomainsStepVisible && /*#__PURE__*/React.createElement(BngDomainSuggestionsSection, _extends({}, domainSuggestionsProps, {
    headingRef: domainsHeadingRef,
    onBackToNames: twoStep ? handleBackToNames : undefined,
    animateMobileEntrance: twoStep
  }))), shouldDisplayFooter && /*#__PURE__*/React.createElement(Box, {
    gridArea: "footer",
    paddingTop: "SP6",
    paddingBottom: "SP6",
    paddingLeft: isMobile ? 'SP4' : '76px',
    paddingRight: isMobile ? 'SP4' : '48px',
    width: "100%",
    boxSizing: "border-box",
    align: "left"
  }, /*#__PURE__*/React.createElement(AnonymousFooter, {
    taxesNotIncluded: taxesNotIncluded
  })));
};
//# sourceMappingURL=BngResultsPageContentV1.js.map